import React, { useEffect, useState, useCallback } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import WelcomeCustomerModal from "./WelcomeCustomerModal";
import EnvironmentBadge from "./EnvironmentBadge";
import { ViewAsBanner, ViewAsSelect } from "./ViewAsBar";
import {
  House, VideoCamera, Package, UsersFour, SignOut, ShoppingBag, Handshake, Tag, Crown,
  UserCircleGear, ShieldCheck, ChartBar, PaperPlaneTilt, Truck, List, X as XIcon, Warning, Receipt, GitMerge,
} from "@phosphor-icons/react";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: House, testid: "nav-dashboard", perm: "dashboard.view" },
  { to: "/lives", label: "Lives", icon: VideoCamera, testid: "nav-lives", perm: "lives.view" },
  { to: "/loyalty", label: "Fidelidade", icon: Crown, testid: "nav-loyalty", perm: "loyalty.view" },
  { to: "/products", label: "Produtos", icon: Package, testid: "nav-products", perm: "products.view", contractorToo: true },
  { to: "/departments", label: "Departamentos", icon: Tag, testid: "nav-departments", perm: "departments.view" },
  { to: "/customers", label: "Clientes", icon: UsersFour, testid: "nav-customers", perm: "customers.view" },
  { to: "/customers/orphan-handles", label: "@s órfãs", icon: Warning, testid: "nav-orphan-handles", onlyOrphans: true },
  { to: "/customers/merge", label: "Unificação", icon: GitMerge, testid: "nav-merge-handles", onlyMaster: true },
  { to: "/contractors", label: "Contratantes", icon: Handshake, testid: "nav-contractors", perm: "contractors.view" },
  { to: "/reports/contractors", label: "Relatórios", icon: ChartBar, testid: "nav-reports-contractors", perm: "contractor.reports.view", altPerm: "contractors.view" },
  { to: "/reports/pending-by-handle", label: "Pendências @", icon: Warning, testid: "nav-reports-pending-by-handle", perm: "reports.view" },
  { to: "/me/pending-lives", label: "Lives a pagar", icon: Receipt, testid: "nav-my-pending-lives", onlyClient: true },
  { to: "/expedition", label: "Expedição", icon: PaperPlaneTilt, testid: "nav-expedition", perm: "expedition.request", hideForMaster: true },
  { to: "/me/profile", label: "Meu cadastro", icon: UserCircleGear, testid: "nav-my-profile", onlyClient: true },
  { to: "/expedition/admin", label: "Expedição · Painel", icon: Truck, testid: "nav-expedition-admin", perm: "expedition.admin.view", badgeKind: "shipments" },
  { to: "/expedition/customer-boxes", label: "Caixinha de clientes", icon: Package, testid: "nav-customer-boxes", perm: "expedition.admin.view", scopedToo: true },
];

const ADMIN_NAV = [
  { to: "/admin/users", label: "Usuários", icon: UserCircleGear, testid: "nav-users", perm: "admin.users" },
  { to: "/admin/roles", label: "Perfis", icon: ShieldCheck, testid: "nav-roles", perm: "admin.roles" },
];

export default function AppShell({ children }) {
  const { user, logout, hasPerm } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [pendingShipments, setPendingShipments] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);

  // Close mobile drawer on route change
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);
  // Prevent body scroll when drawer open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  const loadBadges = useCallback(async () => {
    if (!user?.is_master && !hasPerm("expedition.admin.view")) return;
    try {
      const { data } = await api.get("/expedition/admin/requests", { params: { status: "pending" } });
      setPendingShipments(data.length);
    } catch (err) { console.warn("badge refresh failed", err); }
  }, [user, hasPerm]);
  useEffect(() => { loadBadges(); }, [loadBadges]);
  useEffect(() => {
    const handler = () => loadBadges();
    window.addEventListener("expedition-changed", handler);
    return () => window.removeEventListener("expedition-changed", handler);
  }, [loadBadges]);

  const visibleNav = NAV.filter(n => {
    if (n.hideForMaster && user?.is_master) return false;
    if (n.contractorToo && user?.contractor_id) return true;
    if (n.scopedToo && (user?.contractor_id || user?.customer_handle)) return true;
    if (n.onlyOrphans) return !!user?.can_view_orphans;
    if (n.onlyMaster) return !!user?.is_master;
    if (n.onlyClient) return !!user?.customer_handle && !user?.is_master;
    return hasPerm(n.perm) || (n.altPerm && hasPerm(n.altPerm));
  });
  const visibleAdmin = ADMIN_NAV.filter(n => hasPerm(n.perm));

  return (
    <div className="min-h-screen bg-brand-bg">
      <header className="border-b-2 border-brand-ink bg-white sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16 gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMenuOpen(true)}
              className="md:hidden p-2 border-2 border-brand-ink bg-white hover:bg-brand-yellow shrink-0"
              aria-label="Abrir menu"
            >
              <List weight="bold" size={18} />
            </button>
            <button
              data-testid="logo-home-btn"
              onClick={() => navigate(hasPerm("dashboard.view") ? "/dashboard" : "/expedition")}
              className="flex items-center gap-2 min-w-0"
            >
              <div className="w-9 h-9 bg-brand-pink border-2 border-brand-ink shadow-brutalSm flex items-center justify-center shrink-0">
                <ShoppingBag weight="bold" size={18} color="#F8F0D8" />
              </div>
              <span className="font-heading text-xl font-black truncate">LiveHub</span>
            </button>
            <EnvironmentBadge />
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <ViewAsSelect />
            <div className="hidden sm:flex items-center gap-2 pr-3 border-r-2 border-brand-ink">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-8 h-8 border-2 border-brand-ink" />
              ) : (
                <div className="w-8 h-8 bg-brand-purple border-2 border-brand-ink" />
              )}
              <div className="text-right">
                <p data-testid="user-name" className="text-sm font-bold leading-none">{user?.name}</p>
                <p className="text-[11px] text-brand-ink2">
                  {user?.email}
                  {user?.role_name && ` · ${user.role_name}`}
                </p>
              </div>
            </div>
            <button
              data-testid="logout-btn"
              onClick={logout}
              className="btn-white !px-3 !py-2"
              title="Sair"
            >
              <SignOut weight="bold" size={16} />
              <span className="hidden sm:inline">Sair</span>
            </button>
          </div>
        </div>
        <ViewAsBanner />
      </header>

      {/* Mobile drawer */}
      {menuOpen && (
        <div
          data-testid="mobile-menu-backdrop"
          className="md:hidden fixed inset-0 bg-brand-ink/60 z-40"
          onClick={() => setMenuOpen(false)}
        />
      )}
      <aside
        data-testid="mobile-menu-drawer"
        className={`md:hidden fixed top-0 left-0 h-full w-[82%] max-w-[320px] bg-brand-bg border-r-2 border-brand-ink z-50 transform transition-transform duration-200 ease-out ${
          menuOpen ? "translate-x-0" : "-translate-x-full"
        } flex flex-col`}
      >
        <div className="flex items-center justify-between h-16 px-4 border-b-2 border-brand-ink bg-white shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-9 h-9 border-2 border-brand-ink shrink-0" />
            ) : (
              <div className="w-9 h-9 bg-brand-purple border-2 border-brand-ink shrink-0" />
            )}
            <div className="min-w-0">
              <p className="text-sm font-bold leading-none truncate">{user?.name}</p>
              <p className="text-[10px] text-brand-ink2 truncate">{user?.role_name || user?.email}</p>
            </div>
          </div>
          <button
            data-testid="mobile-menu-close"
            onClick={() => setMenuOpen(false)}
            className="p-2 border-2 border-brand-ink bg-white hover:bg-brand-peach shrink-0"
            aria-label="Fechar menu"
          >
            <XIcon weight="bold" size={16} />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto p-3 space-y-1">
          <ul className="flex flex-col gap-1">
            {visibleNav.map(({ to, label, icon: Icon, testid, badgeKind }) => {
              const showBadge = badgeKind === "shipments" && pendingShipments > 0;
              return (
                <li key={to}>
                  <NavLink
                    to={to}
                    data-testid={`${testid}-mobile`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-3 font-bold text-sm border-2 transition-all ${
                        isActive
                          ? "bg-brand-pink text-white border-brand-ink shadow-brutalSm"
                          : "border-transparent text-brand-ink2 hover:text-brand-ink hover:bg-brand-yellow"
                      }`
                    }
                  >
                    <Icon weight="bold" size={18} />
                    <span className="flex-1">{label}</span>
                    {showBadge && (
                      <span className="text-[10px] font-black bg-brand-yellow text-brand-ink border-2 border-brand-ink px-1.5 rounded-full">
                        {pendingShipments}
                      </span>
                    )}
                  </NavLink>
                </li>
              );
            })}
          </ul>
          {visibleAdmin.length > 0 && (
            <>
              <div className="border-t-2 border-brand-ink my-3" />
              <p className="px-3 pb-1 label-brutal !text-[10px] !mb-0">Administração</p>
              <ul className="flex flex-col gap-1">
                {visibleAdmin.map(({ to, label, icon: Icon, testid }) => (
                  <li key={to}>
                    <NavLink
                      to={to}
                      data-testid={`${testid}-mobile`}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-3 font-bold text-sm border-2 transition-all ${
                          isActive
                            ? "bg-brand-pink text-white border-brand-ink shadow-brutalSm"
                            : "border-transparent text-brand-ink2 hover:text-brand-ink hover:bg-brand-yellow"
                        }`
                      }
                    >
                      <Icon weight="bold" size={18} />
                      {label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </>
          )}
        </nav>
        <button
          data-testid="mobile-menu-logout"
          onClick={() => { setMenuOpen(false); logout(); }}
          className="m-3 mt-0 btn-white flex items-center justify-center gap-2 h-11 shrink-0"
        >
          <SignOut weight="bold" size={16} /> Sair
        </button>
      </aside>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 grid grid-cols-1 md:grid-cols-[220px_1fr] gap-6">
        <aside className="hidden md:block">
          <nav className="card-brutal p-2 md:sticky md:top-24 space-y-1">
            <ul className="flex md:flex-col gap-1 overflow-x-auto md:overflow-visible">
              {visibleNav.map(({ to, label, icon: Icon, testid, badgeKind }) => {
                const showBadge = badgeKind === "shipments" && pendingShipments > 0;
                return (
                  <li key={to} className="flex-1 md:flex-none">
                    <NavLink
                      to={to}
                      data-testid={testid}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2.5 font-bold text-sm border-2 whitespace-nowrap transition-all ${
                          isActive
                            ? "bg-brand-pink text-white border-brand-ink shadow-brutalSm"
                            : "border-transparent text-brand-ink2 hover:text-brand-ink hover:bg-brand-yellow"
                        }`
                      }
                    >
                      <Icon weight="bold" size={18} />
                      <span className="flex-1">{label}</span>
                      {showBadge && (
                        <span data-testid="expedition-badge" className="text-[10px] font-black bg-brand-yellow text-brand-ink border-2 border-brand-ink px-1.5 rounded-full">
                          {pendingShipments}
                        </span>
                      )}
                    </NavLink>
                  </li>
                );
              })}
            </ul>

            {visibleAdmin.length > 0 && (
              <>
                <div className="hidden md:block border-t-2 border-brand-ink my-2" />
                <p className="hidden md:block px-3 py-1 label-brutal !text-[10px] !mb-0">Administração</p>
                <ul className="flex md:flex-col gap-1 overflow-x-auto md:overflow-visible">
                  {visibleAdmin.map(({ to, label, icon: Icon, testid }) => (
                    <li key={to} className="flex-1 md:flex-none">
                      <NavLink
                        to={to}
                        data-testid={testid}
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-3 py-2.5 font-bold text-sm border-2 whitespace-nowrap transition-all ${
                            isActive
                              ? "bg-brand-pink text-white border-brand-ink shadow-brutalSm"
                              : "border-transparent text-brand-ink2 hover:text-brand-ink hover:bg-brand-yellow"
                          }`
                        }
                      >
                        <Icon weight="bold" size={18} />
                        {label}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </nav>
        </aside>

        <main className="min-w-0 animate-fade-in">{children}</main>
      </div>
      <WelcomeCustomerModal />
    </div>
  );
}
